// @flow
/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { getPostflopStackOptions } from '../../utils/postflopStacks';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomLink from '../../components/CustomLink/Link';
import routes from '../../constants/routes';
import Card from '../../components/Card';
import MobileSettings from '../../components/MobileSettings';
import { H4 } from '../../components';
import Toggler from '../../components/Toggler';
import Select from '../../components/Select';
import Multicheckbox from '../../components/Multicheckbox';
import Preloader from '../../components/Preloader';
import Diagram from '../../components/Diagram';
import Legend from '../../components/Legend';
import { resultLegend } from '../../utils/resultColors';
import actionTypes from '../../store/postflop/actionTypes';
import { resultsStatKey } from '../../store/postflop/requestState';
import styles from './styles.module.scss';
import postflopStyles from '../Postflop/styles.module.scss';
import {
  getResultsPreviousActionOptions,
  headsUpResultsPositionOptions,
  resultsPositionOptions,
} from '../Postflop/resultsPositionOptions';
import { defaultResultsFilters, getResultsFilterValues, resolveResultsFilters } from './filters';

const tableSizeOptions = [
  { label: 'HU', value: 'HU' },
  { label: '6max', value: '6max' },
];

const boardTypeOptions = [
  { label: 'Unpaired', value: 'unpaired' },
  { label: 'Paired', value: 'paired' },
  { label: 'Straight', value: 'straight' },
];

const handTypeOptions = [
  { label: 'Any', value: '' },
  { label: '1 PAIR', value: 'one_pair' },
  { label: '2 PAIRS', value: 'two_pair' },
  { label: 'TRIPS+', value: 'trips' },
  { label: 'STRAIGHT', value: 'straight' },
  { label: 'FLUSH', value: 'flush' },
  { label: 'SD', value: 'sd' },
  { label: 'FD', value: 'fd' },
  { label: 'BDFD', value: 'bdfd' },
];

const getPercent = (item) => item?.percents || item?.Percent || item?.percent || 0;

const getStatMeta = (item, key) => item?.[key] || item?.[key.charAt(0).toUpperCase() + key.slice(1)] || 0;

const getFirstStat = (items = []) => (Array.isArray(items) && items.length ? items[0] : null);

const PostflopResults = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const resultsStat = useSelector((state) => state.postflop.resultsStat);
  const requestState = useSelector((state) => state.postflop.requests?.resultsStat);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [isPlo5, setPlo5] = useState(sessionStorage.getItem('isPlo5') === 'true');
  const [filters, setFilters] = useState(defaultResultsFilters);
  const [boardType, setBoardType] = useState({ label: 'Unpaired', value: 'unpaired' });
  const [handType, setHandType] = useState({ label: 'Any', value: '' });
  const selection = resolveResultsFilters(filters, isPlo5, boardType.value, handType.value);
  const {
    stackSize, positionType, pfrType, playerPosition, position,
    positionTypeOptions: availablePositionTypes,
    pfrTypeOptions: availablePfrTypes,
    playerPositionOptions: availablePlayerPositions,
    positionOptions, basePositionOptions, previousActionOptions,
  } = selection;
  const tableSize = tableSizeOptions.find((option) => option.value === selection.tableSize);
  const stackSizeOptions = getPostflopStackOptions(isPlo5, tableSize.value);
  const selectedPreviousAction = previousActionOptions.find((option) => option.value === position.value);
  const requestPayload = {
    isPlo5,
    game: tableSize.value,
    bb: stackSize.value,
    position_type: positionType.value,
    position: position?.value,
    board_type: boardType.value,
    hand_combo_type: handType.value,
  };
  const requestKey = resultsStatKey(requestPayload);
  const isCurrentSelection = requestState?.key === requestKey;
  const isLoading = !isCurrentSelection || requestState?.status === 'loading';
  const error = isCurrentSelection && requestState?.status === 'error' ? requestState.error : '';
  const hasResults = isCurrentSelection && requestState?.status === 'success';

  const updateFilter = (key, option) => {
    if (!option) return;
    setFilters((current) => {
      const normalized = getResultsFilterValues(resolveResultsFilters(current, isPlo5, boardType.value, handType.value));
      return getResultsFilterValues(resolveResultsFilters({ ...normalized, [key]: option.value }, isPlo5, boardType.value, handType.value));
    });
  };
  const setTableSize = (option) => updateFilter('tableSize', option);
  const setStackSize = (option) => updateFilter('stackSize', option);
  const handlePositionType = (option) => updateFilter('positionType', option);
  const handlePfrType = (option) => updateFilter('pfrType', option);
  const handlePlayerPosition = (option) => updateFilter('playerPosition', option);
  const handlePreviousAction = (option) => updateFilter('position', option);
  const handleSpot = (option) => {
    if (!basePositionOptions.some((item) => item.value === option.value)) return;
    const actions = getResultsPreviousActionOptions(
      positionType.value, pfrType.value, playerPosition.value, option.value,
      tableSize.value === 'HU' ? headsUpResultsPositionOptions : resultsPositionOptions,
    ).filter((item) => positionOptions.some((positionOption) => positionOption.value === item.value));
    const nextAction = actions.find((item) => item.action === selectedPreviousAction?.action) || actions[0];
    if (nextAction) handlePreviousAction(nextAction);
  };

  useEffect(() => {
    dispatch({ type: actionTypes.SET_DEFAULT });

    function checkPlo5() {
      setPlo5(sessionStorage.getItem('isPlo5') === 'true');
    }

    window.addEventListener('storage', checkPlo5);

    return () => {
      window.removeEventListener('storage', checkPlo5);
      dispatch({ type: actionTypes.SET_DEFAULT });
    };
  }, []);

  useEffect(() => {
    dispatch({
      type: actionTypes.GET_POSTFLOP_RESULTS_STAT,
      payload: requestPayload,
    });
  }, [
    isPlo5,
    tableSize.value,
    stackSize.value,
    positionType.value,
    position?.value,
    boardType.value,
    handType.value,
    retryAttempt,
  ]);

  const renderStatCard = (title, items = []) => {
    const stat = getFirstStat(items);

    if (!stat) {
      return (
        <section className={styles.statPanel}>
          <div className={styles.statPanelHeader}>
            <h2>{title}</h2>
          </div>
          <div className={styles.emptyState}>
            <div className={styles.empty}>No results yet</div>
          </div>
        </section>
      );
    }

    const percent = getPercent(stat);
    const streak = getStatMeta(stat, 'streak');
    const counter = getStatMeta(stat, 'counter');

    return (
      <section className={styles.statPanel}>
        <div className={styles.statPanelHeader}>
          <h2>{title}</h2>
        </div>
        <div className={styles.statPanelBody}>
          <div className={styles.diagramSlot}>
            <Diagram
              percent={percent}
              base={100}
              className={styles.statDiagram}
            />
          </div>
          <div className={styles.statDetails}>
            <div className={styles.statMetric}>
              <span className={styles.metricLabel}>Accuracy</span>
              <span className={styles.metricValue}>{`${percent}%`}</span>
            </div>
            <div className={styles.statMetric}>
              <span className={styles.metricLabel}>Counter</span>
              <span className={styles.metricValue}>{counter}</span>
            </div>
            <div className={styles.statMetric}>
              <span className={styles.metricLabel}>Streak</span>
              <span className={styles.metricValue}>{streak}</span>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className={classnames(postflopStyles.trainingGame, styles.resultsPage)}>
      <MobileSettings title="Result filters">
      <Card className={postflopStyles.controlPanelWrapper}>
        <div className={postflopStyles.controlPanelWrap}>
          <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
            <H4>Table size</H4>
            <Toggler
              selected={tableSize}
              options={tableSizeOptions}
              onToggle={setTableSize}
              className={classnames(postflopStyles.gameTogglerColor, postflopStyles.firstToggler)}
              controlToggler
            />
            <Select
              className={postflopStyles.adaptiveSelectComponent}
              value={tableSize}
              options={tableSizeOptions}
              onChange={setTableSize}
            />
          </div>
          <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
            <H4>Stack size</H4>
            <Toggler
              selected={stackSize}
              options={stackSizeOptions}
              onToggle={setStackSize}
              className={classnames(postflopStyles.gameTogglerColor, postflopStyles.firstToggler)}
              controlToggler
            />
            <Select
              className={postflopStyles.adaptiveSelectComponent}
              value={stackSize}
              options={stackSizeOptions}
              onChange={setStackSize}
            />
          </div>
          <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
            <H4>Position Type</H4>
            <Toggler
              selected={positionType}
              options={availablePositionTypes}
              onToggle={handlePositionType}
              className={classnames(postflopStyles.gameTogglerColor, postflopStyles.firstToggler)}
              controlToggler
            />
            <Select
              className={postflopStyles.adaptiveSelectComponent}
              value={positionType}
              options={availablePositionTypes}
              onChange={handlePositionType}
            />
          </div>
          {(
            <>
              <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
                <H4>PFR</H4>
                <Toggler
                  selected={pfrType}
                  options={availablePfrTypes}
                  onToggle={handlePfrType}
                  className={classnames(postflopStyles.gameTogglerColor, postflopStyles.firstToggler)}
                  controlToggler
                />
                <Select
                  className={postflopStyles.adaptiveSelectComponent}
                  value={pfrType}
                  options={availablePfrTypes}
                  onChange={handlePfrType}
                />
              </div>
              <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
                <H4>Position</H4>
                <Toggler
                  selected={playerPosition}
                  options={availablePlayerPositions}
                  onToggle={handlePlayerPosition}
                  className={classnames(postflopStyles.gameTogglerColor, postflopStyles.firstToggler)}
                  controlToggler
                />
                <Select
                  className={postflopStyles.adaptiveSelectComponent}
                  value={playerPosition}
                  options={availablePlayerPositions}
                  onChange={handlePlayerPosition}
                />
              </div>
            </>
          )}
          <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
            <H4>Board Type</H4>
            <Toggler
              selected={boardType}
              options={boardTypeOptions}
              onToggle={setBoardType}
              className={classnames(postflopStyles.gameTogglerColor, postflopStyles.firstToggler)}
              controlToggler
            />
            <Select
              className={postflopStyles.adaptiveSelectComponent}
              value={boardType}
              options={boardTypeOptions}
              onChange={setBoardType}
            />
          </div>
          <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
            <H4>Hand Type</H4>
            <Select
              value={handType}
              options={handTypeOptions}
              onChange={setHandType}
              placeholder="Any hand type"
            />
          </div>
          <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
            <H4>Spot</H4>
            <div className={classnames(postflopStyles.resultsSpotWrapper, styles.spotList)}>
              <Multicheckbox
                options={basePositionOptions}
                selectedOptions={position?.value ? [position.value.split('_')[0]] : []}
                setSelectedOptions={handleSpot}
                forTrickyCards
              />
            </div>
          </div>
          {(
            <div className={classnames(postflopStyles.controlPanelLimit, postflopStyles.control)}>
              <H4>Situation</H4>
              <Select
                value={selectedPreviousAction}
                options={previousActionOptions}
                onChange={handlePreviousAction}
              />
            </div>
          )}
        </div>
      </Card>
      </MobileSettings>
      <Card className={classnames(postflopStyles.gameWrapper, styles.center)}>
        <div className={styles.resultsContent}>
          <div className={styles.resultsHelp}>
            <CustomLink to={routes.app.resultsFAQ.root}>{t('resultsFaqLabel', 'Results FAQ')}</CustomLink>
          </div>
          <Preloader isLoading={isLoading}>
            {error ? (
              <div className={styles.requestStatus} role="alert">
                <p>Results could not be loaded.</p>
                <p>{error}</p>
                <button type="button" className={styles.retryButton} onClick={() => setRetryAttempt((value) => value + 1)}>
                  Retry
                </button>
              </div>
            ) : hasResults ? (
              <div className={styles.statsGrid}>
                {renderStatCard(t('averageLastMonthLabel', 'Average last month'), resultsStat?.average || [])}
                {renderStatCard('Best', resultsStat?.best || [])}
              </div>
            ) : <div className={styles.requestStatus} role="status">Loading results…</div>}
          </Preloader>
        </div>
        <div className={styles.resultsLegend} aria-label="Result percentage colors">
          {resultLegend.map((item) => (
            <Legend key={item.name} name={item.name} color={item.color} />
          ))}
        </div>
      </Card>
      <Card className={classnames(postflopStyles.resultPanelWrapper, styles.placeholderPanel)}>
        <div className={styles.summaryPanel}>
          {[
            ['Average best spots and situations result', 'completed_best_average'],
            ['Coverage of spots and situations', 'positions_coverage'],
            ['Quality adjusted coverage', 'group_best_average'],
          ].map(([label, key]) => (
            <section className={styles.summaryMetric} key={key} aria-label={label}>
              <h2 className={styles.summaryTitle}>{label}</h2>
              <div className={styles.summaryDiagramSlot}>
                {hasResults ? <Diagram
                  percent={resultsStat?.[key] || 0}
                  base={100}
                  className={styles.summaryDiagram}
                /> : <div className={styles.requestStatus}>{error ? 'Results unavailable' : 'Loading results…'}</div>}
              </div>
            </section>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default PostflopResults;
